import { useEffect, useRef, useState } from "react";
import { Bell, Check } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { merchantApi } from "@/lib/api/merchant";
import { cn } from "@/lib/utils";

type Note = { id: string; subject: string; body: string; readAt: string | null; createdAt: string };

function timeAgo(iso: string) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

/** Header notification bell — polls unread count, panel lists recent + mark-read. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => merchantApi.notifications(),
    refetchInterval: 30_000,
  });
  const notes: Note[] = data?.notifications ?? [];
  const unread: number = data?.unread ?? 0;

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications"] });

  const markRead = async (n: Note) => {
    if (!n.readAt) {
      await merchantApi.notificationRead(n.id);
      refresh();
    }
  };
  const markAll = async () => {
    await merchantApi.notificationReadAll();
    refresh();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative rounded-md p-1.5 text-muted-foreground hover:bg-muted"
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border bg-card shadow-lg">
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-[13px] font-semibold">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="flex items-center gap-1 text-[11px] text-primary hover:underline">
                <Check size={12} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notes.length === 0 ? (
              <div className="px-3 py-8 text-center text-[12px] text-muted-foreground">No notifications</div>
            ) : (
              notes.map((n) => (
                <button
                  key={n.id}
                  onClick={() => markRead(n)}
                  className={cn(
                    "flex w-full flex-col items-start gap-0.5 border-b px-3 py-2.5 text-left last:border-0 hover:bg-muted/50",
                    !n.readAt && "bg-primary/5"
                  )}
                >
                  <div className="flex w-full items-center justify-between gap-2">
                    <span className="truncate text-[12px] font-medium">{n.subject}</span>
                    {!n.readAt && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />}
                  </div>
                  <span className="text-[11px] text-muted-foreground">{n.body}</span>
                  <span className="text-[10px] text-muted-foreground/70">{timeAgo(n.createdAt)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
